import { ChangeEvent, FC, useMemo, useRef } from 'react';

import {
    AvailableCurrencies,
    convertCentsToRubles,
    convertRublesToCents,
    CURRENCY_SYMBOLS,
    formatCost,
} from 'utils/clickme';

import cpcFlexibleBudgetFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/cpc_flexible_budget_form_field_touch';
import cpcFlexibleBudgetRestrictionButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/cpc_flexible_budget_restriction_button_click';
import { Cell, CellText, Checkbox, Text, TextList, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import useIsNewCampaign from 'utils/campaign/hooks/useIsNewCampaign';

import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';
import { MoneyInput } from 'components/common/MoneyInput/MoneyInput';
import { TooltipCommon } from 'components/common/TooltipCommon/TooltipCommon';
import { useMinBudget } from 'components/CpcFlexibleCreate/CpcForm/AdBudget/useMinBudget';

import { setCampaignTotalBudget, setCampaignTotalBudgetNoLimits } from 'store/models/createCampaign';
import { selectMainMenuVacancyId } from 'store/models/mainMenu';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    label: 'AdCreate.AdForm.AdTotalBudget.label',
    info: 'AdCreate.AdForm.AdTotalBudget.info',
    placeholder: 'AdCreate.AdForm.AdTotalBudget.input.placeholder',
    adTotalBudgetNoLimits: {
        label: 'AdCreate.AdForm.adTotalBudgetNoLimits.label',
        info: {
            description: 'AdCreate.AdForm.adTotalBudgetNoLimits.info.description',
            listItem1: 'AdCreate.AdForm.adTotalBudgetNoLimits.info.listItem_1',
            listItem2: 'AdCreate.AdForm.adTotalBudgetNoLimits.info.listItem_2',
            listItem3: 'AdCreate.AdForm.adTotalBudgetNoLimits.info.listItem_3',
        },
    },
};

export const TotalBudget: FC = () => {
    const vacancyId = useSelector(selectMainMenuVacancyId);
    const { adTotalBudgetNoLimits, adTotalBudget, campaignErrors } = useSelector((state) => state.createCampaign);
    const { adTotalBudget: errorAdTotalBudget, adTotalBudgetNoLimits: errorAdTotalBudgetNoLimits } = campaignErrors;
    const isCampaignCreateProcess = useIsNewCampaign();
    const [isShowInfoTip, setShowInfoTip, setHideInfoTip] = useOnOffState(false);
    const [
        isShowAdTotalBudgetNoLimitsInfoTip,
        setShowAdTotalBudgetNoLimitsInfoTip,
        setHideAdTotalBudgetNoLimitsInfoTip,
    ] = useOnOffState(false);

    const budget = useMemo(() => (adTotalBudget === null ? '' : convertRublesToCents(adTotalBudget)), [adTotalBudget]);

    const { minBudget } = useMinBudget();

    const dispatch = useDispatch();
    const trl = useTrls();
    const { isMobile } = useBreakpoint();

    const labelActivatorRef = useRef<HTMLDivElement>(null);
    const adTotalBudgetNoLimitsInfoTipActivatorRef = useRef<HTMLDivElement>(null);

    const onChangeBudget = (value: string) => {
        const valueInRubles = convertCentsToRubles(value, 0);
        dispatch(setCampaignTotalBudget(valueInRubles));
    };

    const onChangeTotalBudgetNoLimits = (event: ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        if (isCampaignCreateProcess) {
            cpcFlexibleBudgetRestrictionButtonClick({
                newCheckboxState: checked,
                vacancyId,
            });
        }
        dispatch(setCampaignTotalBudgetNoLimits(checked));
    };

    return (
        <MagritteFormField
            label={
                <Cell
                    width="fit-content"
                    gap={6}
                    right={
                        <QuestionCircleOutlinedSize16
                            initialColor="secondary"
                            ref={labelActivatorRef}
                            onClick={setShowInfoTip}
                        />
                    }
                >
                    <CellText typography="label-2-regular" style="secondary">
                        {trl(TrlKeys.label)}
                    </CellText>

                    <TooltipCommon
                        activatorRef={labelActivatorRef}
                        tooltipContent={
                            <Text typography={isMobile ? 'label-1-regular' : 'paragraph-2-regular'}>
                                {trl(TrlKeys.info)}
                            </Text>
                        }
                        visible={isShowInfoTip}
                        onClose={setHideInfoTip}
                        placement="right-center"
                        bottomSheetTitle={trl(TrlKeys.label)}
                    />
                </Cell>
            }
            input={
                <>
                    <MoneyInput
                        value={adTotalBudgetNoLimits ? '' : budget}
                        postfix={CURRENCY_SYMBOLS[AvailableCurrencies.RUR]}
                        onChange={onChangeBudget}
                        onFocus={() => {
                            if (isCampaignCreateProcess) {
                                cpcFlexibleBudgetFormFieldTouch({
                                    vacancyId,
                                });
                            }
                        }}
                        placeholder={trl(TrlKeys.placeholder, [formatCost(minBudget)])}
                        invalid={!!(errorAdTotalBudget || errorAdTotalBudgetNoLimits)}
                        errorMessage={errorAdTotalBudget || errorAdTotalBudgetNoLimits}
                        data-qa="total-budget-input"
                        clearableOnFocus
                    />
                    <VSpacing default={12} />
                    <Cell
                        width="fit-content"
                        left={
                            <Checkbox
                                onChange={onChangeTotalBudgetNoLimits}
                                checked={adTotalBudgetNoLimits}
                                invalid={Boolean(campaignErrors.adTotalBudgetNoLimits)}
                                data-qa="total-budget-no-limits-checkbox"
                            />
                        }
                        right={
                            <QuestionCircleOutlinedSize16
                                initialColor="secondary"
                                ref={adTotalBudgetNoLimitsInfoTipActivatorRef}
                                onClick={setShowAdTotalBudgetNoLimitsInfoTip}
                            />
                        }
                    >
                        <CellText>{trl(TrlKeys.adTotalBudgetNoLimits.label)}</CellText>
                    </Cell>
                    <TooltipCommon
                        bottomSheetTitle={trl(TrlKeys.adTotalBudgetNoLimits.label)}
                        activatorRef={adTotalBudgetNoLimitsInfoTipActivatorRef}
                        placement="right-center"
                        tooltipContent={
                            <>
                                <Text typography={isMobile ? 'label-1-regular' : 'paragraph-2-regular'}>
                                    {trl(TrlKeys.adTotalBudgetNoLimits.info.description)}
                                </Text>
                                <VSpacing default={8} />
                                <TextList>
                                    <Text>{trl(TrlKeys.adTotalBudgetNoLimits.info.listItem1)}</Text>
                                    <Text>{trl(TrlKeys.adTotalBudgetNoLimits.info.listItem2)}</Text>
                                    <Text>{trl(TrlKeys.adTotalBudgetNoLimits.info.listItem3)}</Text>
                                </TextList>
                            </>
                        }
                        visible={isShowAdTotalBudgetNoLimitsInfoTip}
                        onClose={setHideAdTotalBudgetNoLimitsInfoTip}
                    />
                </>
            }
        />
    );
};
